<template>
  <div>
    <b-dropdown
      class="float-right"
      variant="link"
      toggle-class="text-decoration-none"
      no-caret
      right
    >
      <template #button-content>
        <span class="svg-icon svg-icon-lg">
          <inline-svg src="media/svg/icons/General/Menu.svg" />
        </span>
      </template>
      <b-dropdown-item @click="CSV()">CSV</b-dropdown-item>
      <b-dropdown-item @click="saveImg()">PNG</b-dropdown-item>
    </b-dropdown>
    <br /><br /><br />
    <v-chart ref="chartRef" class="chart" :option="option" />
  </div>
</template>

<script>
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent
} from "echarts/components";
import VChart, { THEME_KEY } from "vue-echarts";

import "echarts/lib/component/grid";

import Swal from "sweetalert2";
import { DASHBOARD_CUSTOMER_LINE_EXPORT_CAMPAIGN } from "@/core/services/store/campaign.module";

use([
  CanvasRenderer,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent
]);

export default {
  name: "CampaignLineChart",
  components: {
    VChart
  },
  provide: {
    [THEME_KEY]: "light"
  },
  props: {
    data: {
      type: Object,
      default: null
    },
    isProPlan: {
      type: Boolean,
      default: false
    },
    campaignId: {
      type: [String, Number],
      default: null
    },
    filterFrom: {
      type: Date,
      default: null
    },
    filterTo: {
      type: Date,
      default: null
    }
  },
  data() {
    return {
      option: {
        // title: {
        // text: `${this.$t("PAGE.DASHBOARD.AMOUNT_OF_WASTE_TITLE")}`,
        // left: "center"
        // },
        tooltip: {
          trigger: "item",
          formatter: (params) => {
            let shortMonths = [
              "ม.ค.",
              "ก.พ.",
              "มี.ค.",
              "เม.ย.",
              "พ.ค.",
              "มิ.ย.",
              "ก.ค.",
              "ส.ค.",
              "ก.ย.",
              "ต.ค.",
              "พ.ย.",
              "ธ.ค."
            ];
            let newDate = new Date(params.name);
            let dateStr = `${newDate.getDate()} ${
              shortMonths[newDate.getMonth()]
            } ${newDate.getFullYear()}`;
            return `
              ${dateStr}</br>
              ${params.seriesName} : ${params.value}`;
          }
        },
        legend: {
          bottom: "bottom",
          type: "scroll",
          height: "200"
        },
        grid: {
          left: "15%",
          right: "15%",
          bottom: "20%",
          containLabel: false
        },
        xAxis: [
          {
            name: `${this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CAMPAIGN_LINE_X"
            )}`,
            type: "category",
            data: [],
            axisLabel: {
              formatter: function (value) {
                let shortMonths = [
                  "ม.ค.",
                  "ก.พ.",
                  "มี.ค.",
                  "เม.ย.",
                  "พ.ค.",
                  "มิ.ย.",
                  "ก.ค.",
                  "ส.ค.",
                  "ก.ย.",
                  "ต.ค.",
                  "พ.ย.",
                  "ธ.ค."
                ];
                let newDate = new Date(value);
                return `${newDate.getDate()} ${
                  shortMonths[newDate.getMonth()]
                } ${newDate.getFullYear()}`;
              },
              rotate: 60
            }
          }
        ],
        yAxis: [
          {
            name: `${this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CAMPAIGN_LINE_Y"
            )}`,
            type: "value"
          }
        ],
        series: [],
        color: ["#008ffb", "#00e396"],
        backgroundColor: "white"
      }
    };
  },
  mounted() {
    if (this.data) {
      let seriesData = [];
      for (let index = 0; index < this.data.series.length; index++) {
        const element = this.data.series[index];
        seriesData.push({
          name: element.name,
          type: "bar",
          stack: element.name,
          emphasis: {
            focus: "series"
          },
          data: element.data
        });
      }
      this.option.xAxis[0].data = this.data.categories;
      this.option.series = seriesData;
    }
  },
  methods: {
    csvEscapeCell(val) {
      const s = String(val == null ? "" : val);
      if (/[",\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    },
    apiCatchMessage(error) {
      if (error && error.message) return error.message;
      if (error && error.response && error.response.data) {
        const d = error.response.data;
        if (typeof d === "string") return d;
        if (d.message) return d.message;
        if (d.error) return d.error;
      }
      return error != null ? String(error) : "Unknown error";
    },
    async CSV() {
      if (this.isProPlan && this.campaignId != null && this.campaignId !== "") {
        await this.exportCsvPro();
        return;
      }
      const exportCategories = this.option.xAxis[0].data || [];
      const exportSeries = this.option.series || [];

      if (!exportCategories.length || !exportSeries.length) {
        return;
      }
      let headers = [
        `${this.$t(
          "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CAMPAIGN_LINE_X"
        )}`
      ];

      for (let index = 0; index < exportSeries.length; index++) {
        const element = exportSeries[index];
        headers.push(element.name);
      }

      let contents = [];
      for (let index = 0; index < exportCategories.length; index++) {
        const element = exportCategories[index];
        contents.push([this.csvEscapeCell(element)]);
      }

      for (let index = 0; index < exportSeries.length; index++) {
        const element = exportSeries[index];
        for (let index2 = 0; index2 < element.data.length; index2++) {
          const element2 = element.data[index2];
          contents[index2].push(element2);
        }
      }

      // prepare CSV data
      let csvData = [];
      csvData.push(headers);
      for (let index = 0; index < contents.length; index++) {
        const element = contents[index];
        csvData.push(element);
      }

      let lineArray = [];
      csvData.forEach(function (infoArray, index) {
        let line = infoArray.join(",");
        lineArray.push(index == 0 ? "\uFEFF" + line : line);
      });
      let csvContent = lineArray.join("\n");

      // download stuff
      let blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      let link = document.createElement("a");

      if (link.download !== undefined) {
        // feature detection
        // Browsers that support HTML5 download attribute
        link.setAttribute("href", window.URL.createObjectURL(blob));
        link.setAttribute(
          "download",
          `${this.$t(
            "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CAMPAIGN_LINE_Y"
          )}.csv`
        );
        link.setAttribute("hidden", true);
      } else {
        // it needs to implement server side export
        link.setAttribute("href", "#");
      }
      link.click();
    },
    async exportCsvPro() {
      try {
        const { data } = await this.$store.dispatch(
          DASHBOARD_CUSTOMER_LINE_EXPORT_CAMPAIGN,
          {
            id: this.campaignId,
            params: {
              start_date: this.filterFrom,
              end_date: this.filterTo
            }
          }
        );
        if (data.status === "success" && data.data && data.data.rows) {
          const rows = data.data.rows;
          const headers = [
            this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CAMPAIGN_LINE_X"
            ),
            this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CUSTOMER_LINE_EXPORT_USER_COUNT"
            ),
            this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CUSTOMER_LINE_EXPORT_RECEIPT_COUNT"
            ),
            this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CUSTOMER_LINE_EXPORT_USER_NAMES"
            )
          ];
          const csvData = [headers];
          for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            const dk = r.date || "";
            const parts = dk.split("-");
            let dateCell = dk;
            if (parts.length === 3) {
              dateCell = `${parts[2]}/${parts[1]}/${parts[0]}`;
            }
            csvData.push([
              this.csvEscapeCell(dateCell),
              this.csvEscapeCell(r.user_count),
              this.csvEscapeCell(r.receipt_count),
              this.csvEscapeCell(r.names)
            ]);
          }
          const lineArray = [];
          csvData.forEach(function (infoArray, index) {
            const line = infoArray.join(",");
            lineArray.push(index === 0 ? "\uFEFF" + line : line);
          });
          const csvContent = lineArray.join("\n");
          const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;"
          });
          const link = document.createElement("a");
          if (link.download !== undefined) {
            link.setAttribute("href", window.URL.createObjectURL(blob));
            link.setAttribute(
              "download",
              `${this.$t(
                "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CAMPAIGN_LINE_Y"
              )}_pro_detail.csv`
            );
            link.setAttribute("hidden", true);
          } else {
            link.setAttribute("href", "#");
          }
          link.click();
        } else if (data.status === "error" && data.error) {
          Swal.fire({
            icon: "error",
            text: `${this.$t("RESPONSE_MESSAGE." + data.error)}`,
            showConfirmButton: true,
            confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          text: this.apiCatchMessage(error),
          showConfirmButton: true,
          confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
        });
      }
    },
    saveImg() {
      const chartRef = this.$refs.chartRef;
      const chartInstance =
        chartRef && chartRef.chart ? chartRef.chart : chartRef;
      let imageDataUrl = null;

      if (chartInstance && typeof chartInstance.getDataURL === "function") {
        imageDataUrl = chartInstance.getDataURL({
          type: "png",
          pixelRatio: 2,
          backgroundColor: "#ffffff"
        });
      }

      if (!imageDataUrl) {
        const canvas = this.$el.querySelector("canvas");
        if (canvas) {
          imageDataUrl = canvas.toDataURL("image/png");
        }
      }

      if (imageDataUrl) {
        let tempA = document.createElement("a");
        tempA.download = `${this.$t(
          "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CAMPAIGN_LINE_Y"
        )}.png`;
        tempA.href = imageDataUrl;
        document.body.appendChild(tempA);
        tempA.click();
        tempA.remove();
      }
    }
  }
};
</script>

<style scoped>
.chart {
  height: 600px;
}
</style>

<style>
body {
  margin: 0;
}
</style>
